/**
 * GA4 telemetry client for the desktop application.
 *
 * Wires the global `gtag.js` script (lazy loaded once from the Google Tag
 * Manager CDN) and exposes two ergonomic helpers — `trackEvent` for fire-
 * and-forget events and `setUserId` for identity. Both no-op silently when
 * no measurement id is configured (eg. in development without env) so call
 * sites don't have to guard.
 *
 * Configuration: reads `import.meta.env.VITE_GA_MEASUREMENT_ID` (Vite env)
 * to match the codebase pattern (`VITE_BACKEND_URL`, `VITE_WEB_URL`, ...).
 *
 * Initialise once at app startup via `initAnalytics()`. After that any
 * module can call `trackEvent("overlay_apply", { ... })` directly.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const SCRIPT_ID = "ga4-gtag-loader";

let initialized = false;
let measurementId: string | null = null;

const readMeasurementId = (): string | null => {
  try {
    const env = import.meta.env as Record<string, string | undefined>;
    const id = env?.VITE_GA_MEASUREMENT_ID;
    if (typeof id === "string" && id.trim().length > 0) {
      return id.trim();
    }
  } catch {
    /* import.meta.env not available — fall through */
  }
  return null;
};

/**
 * Idempotently loads gtag.js and runs the GA4 boot sequence. Safe to call
 * multiple times; only the first invocation actually attaches the script.
 * No-ops if `VITE_GA_MEASUREMENT_ID` isn't configured.
 */
export const initAnalytics = (): void => {
  if (initialized) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const id = readMeasurementId();
  if (!id) {
    // Dev / un-configured environments: skip silently.
    return;
  }

  measurementId = id;

  // Avoid attaching twice if a previous module/page injected the loader.
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
  }

  window.dataLayer = window.dataLayer || [];
  // gtag is implemented by the loader as a thin push() wrapper. We provide
  // an inline shim so events queued before the script lands still flow once
  // the script attaches its own implementation.
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]): void {
      window.dataLayer!.push(args);
    };
  }

  window.gtag("js", new Date());
  window.gtag("config", id, {
    // The desktop app is not a website — disable automatic page_view so we
    // only collect explicit events. Custom dimensions can still be sent.
    send_page_view: false,
  });

  initialized = true;
};

/**
 * Emits a GA4 event with the given parameters. No-ops silently if gtag
 * isn't available yet (eg. before `initAnalytics` ran or if MEASUREMENT_ID
 * is unset). Errors thrown by the gtag stub are swallowed — telemetry must
 * never crash a user flow.
 */
export const trackEvent = (
  name: string,
  params?: Record<string, unknown>,
): void => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag("event", name, params ?? {});
  } catch {
    /* swallow */
  }
};

/**
 * Associates subsequent events with a user id (or clears it on `null`).
 * Safe to call before init; just no-ops in that case.
 */
export const setUserId = (userId: string | null): void => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag("set", { user_id: userId ?? undefined });
  } catch {
    /* swallow */
  }
};

/** Test/internal: expose the resolved measurement id (or null). */
export const getMeasurementId = (): string | null => measurementId;
